// Type + engine aware operator suggester.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2.
//
// Single entry point for IntellisenseEngine + VQE consumer surfaces. Routes
// on the operand's syntactic kind first (column / literal / expression), then
// on its semantic shape (jsonClassification for column + expression arms;
// sqlType for literal arm). Engines fold in only when the operand resolves to
// a JSON shape — non-JSON lattices are engine-agnostic in v1.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { JsonClassification, OperandType } from '../../types';
import { mssqlJsonOperatorsFor } from './dialect/mssql';
import { mysqlJsonOperatorsFor } from './dialect/mysql';
import { oracleJsonOperatorsFor } from './dialect/oracle';
import { postgresJsonOperatorsFor } from './dialect/postgres';
import { sqliteJsonOperatorsFor } from './dialect/sqlite';
import {
  BOOLEAN_OPERATORS,
  GENERIC_COLUMN_OPERATORS,
  NULL_OPERATORS,
  NUMERIC_OPERATORS,
  TEXT_OPERATORS,
} from './standardLattices';
import type { Operator } from './types';

function jsonOperatorsForDialect(
  dialect: SqlEngine,
  classification: JsonClassification,
): readonly Operator[] {
  switch (dialect) {
    case 'mssql':
      return mssqlJsonOperatorsFor();
    case 'postgres':
      return postgresJsonOperatorsFor(classification);
    case 'mysql':
      return mysqlJsonOperatorsFor();
    case 'oracle':
      return oracleJsonOperatorsFor();
    case 'sqlite':
      return sqliteJsonOperatorsFor();
  }
}

function isJsonClassified(classification: JsonClassification | undefined): boolean {
  return classification !== undefined && classification.kind !== 'not-json';
}

export function suggestOperatorsForOperand(
  operand: OperandType,
  dialect: SqlEngine,
): readonly Operator[] {
  if (operand.kind === 'column') {
    if (isJsonClassified(operand.jsonClassification)) {
      return jsonOperatorsForDialect(dialect, operand.jsonClassification!);
    }
    // Non-JSON column: route on the coarse column type when the caller supplied
    // it (VQE threads `column.dataType` → `columnSqlType`). A `text` column thus
    // surfaces `LIKE`, numeric columns surface `BETWEEN`, etc. — mirroring the
    // literal arm below. Callers without schema in scope (IntelliSense) leave
    // `columnSqlType` undefined and keep the engine-agnostic generic fallback.
    switch (operand.columnSqlType) {
      case 'integer':
      case 'numeric':
        return NUMERIC_OPERATORS;
      case 'text':
        return TEXT_OPERATORS;
      case 'boolean':
        return BOOLEAN_OPERATORS;
      default:
        return GENERIC_COLUMN_OPERATORS;
    }
  }
  if (operand.kind === 'literal') {
    switch (operand.sqlType) {
      case 'integer':
      case 'numeric':
        return NUMERIC_OPERATORS;
      case 'text':
        return TEXT_OPERATORS;
      case 'boolean':
        return BOOLEAN_OPERATORS;
      case 'null':
        return NULL_OPERATORS;
      case 'unknown':
        return GENERIC_COLUMN_OPERATORS;
    }
  }
  // expression
  if (isJsonClassified(operand.resultClassification)) {
    return jsonOperatorsForDialect(dialect, operand.resultClassification!);
  }
  // Inferred result type (CAST targets, function returns, arithmetic) routes
  // the same way the literal / column arms do — `CAST(x AS INT) ▮` surfaces
  // numeric operators, `UPPER(c) ▮` surfaces text operators. Absent → GENERIC.
  switch (operand.resultSqlType) {
    case 'integer':
    case 'numeric':
      return NUMERIC_OPERATORS;
    case 'text':
      return TEXT_OPERATORS;
    case 'boolean':
      return BOOLEAN_OPERATORS;
    default:
      return GENERIC_COLUMN_OPERATORS;
  }
}
